import { useEffect, useState } from 'react';
import { Building2, KeyRound, Pencil, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { entitiesAPI } from '../services/api';

const initialForm = {
  code: '', name: '', category: 'office', country_code: 'IN', timezone: 'Asia/Kolkata',
  admin_name: '', admin_email: '', admin_password: '',
  lifecycle_status: 'active', subscription_starts_on: '', subscription_ends_on: '',
  licensed_modules: ['attendance', 'reports', 'rfid'],
  max_users: 500, max_readers: 50, max_cameras: 50, max_edge_agents: 10,
};
const availableModules = ['attendance', 'reports', 'rfid', 'face', 'cameras', 'analytics'];

const Entities = () => {
  const [entities, setEntities] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [countries, setCountries] = useState([]);

  const load = () => entitiesAPI.getAll()
    .then((response) => setEntities(response.data.entities || []))
    .catch((error) => toast.error(error.response?.data?.error || 'Failed to load entities'));

  useEffect(() => {
    load();
    entitiesAPI.getLocaleOptions()
      .then((response) => setCountries(response.data.countries || []))
      .catch((error) => toast.error(error.response?.data?.error || 'Failed to load countries and timezones'));
  }, []);

  const selectedCountry = countries.find((country) => country.code === form.country_code);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await entitiesAPI.update(editingId, form);
        toast.success('Entity controls updated');
      } else {
        await entitiesAPI.create(form);
        toast.success('Entity and administrator created');
      }
      setForm(initialForm);
      setEditingId(null);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create entity');
    } finally {
      setSaving(false);
    }
  };

  const edit = (entity) => {
    setEditingId(entity.id);
    setForm({ ...initialForm, ...entity,
      subscription_starts_on: entity.subscription_starts_on?.slice(0, 10) || '',
      subscription_ends_on: entity.subscription_ends_on?.slice(0, 10) || '',
      licensed_modules: entity.licensed_modules || [],
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetPassword = async (entity) => {
    const password = window.prompt(`Enter a new password for ${entity.name}'s administrator (minimum 12 characters):`);
    if (!password) return;
    try { await entitiesAPI.resetAdminPassword(entity.id, password); toast.success('Administrator password reset'); }
    catch (error) { toast.error(error.response?.data?.error || 'Password reset failed'); }
  };

  return <div className="space-y-6">
    <div className="app-hero rounded-[2rem] px-6 py-6 md:px-8">
      <p className="app-kicker">Platform Administration</p>
      <h1 className="mt-2 text-3xl font-semibold">Entities</h1>
      <p className="mt-2 text-sm text-white/75">Each entity has isolated users, attendance, devices, cameras and reports.</p>
    </div>
    <form onSubmit={submit} className="app-surface grid gap-4 rounded-[1.75rem] p-6 md:grid-cols-2 xl:grid-cols-3">
      {[
        ['name', 'Entity name', 'Organization name'], ['code', 'Entity code', 'example-school'],
        ['admin_name', 'Administrator name', 'Full name'],
        ['admin_email', 'Administrator email', 'admin@example.com'], ['admin_password', 'Initial password', 'Minimum 12 characters'],
      ].filter(([key]) => !editingId || !key.startsWith('admin_')).map(([key, label, placeholder]) => <label key={key} className="text-sm font-medium text-slate-700">
        {label}
        <input type={key === 'admin_password' ? 'password' : key === 'admin_email' ? 'email' : 'text'}
          value={form[key]} placeholder={placeholder} required disabled={editingId && key === 'code'}
          minLength={key === 'admin_password' ? 12 : undefined}
          onChange={(event) => setForm({ ...form, [key]: key === 'code' ? event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') : event.target.value })}
          className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-teal-500" />
      </label>)}
      <label className="text-sm font-medium text-slate-700">Country
        <select value={form.country_code} required
          onChange={(event) => {
            const country = countries.find((item) => item.code === event.target.value);
            setForm({ ...form, country_code: event.target.value, timezone: country?.default_timezone || 'UTC' });
          }} className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3">
          {countries.map((country) => <option key={country.code} value={country.code}>{country.name}</option>)}
        </select>
      </label>
      <label className="text-sm font-medium text-slate-700">Timezone
        <select value={form.timezone} required onChange={(event) => setForm({ ...form, timezone: event.target.value })}
          className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3">
          {(selectedCountry?.timezones || [form.timezone]).map((timezone) => <option key={timezone} value={timezone}>{timezone}</option>)}
        </select>
      </label>
      <label className="text-sm font-medium text-slate-700">Entity category
        <select value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })}
          className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3" required>
          <option value="school">School</option>
          <option value="college">College</option>
          <option value="office">Office</option>
        </select>
      </label>
      {['subscription_starts_on', 'subscription_ends_on'].map((key) => <label key={key} className="text-sm font-medium text-slate-700">
        {key === 'subscription_starts_on' ? 'Subscription starts' : 'Subscription ends'}
        <input type="date" value={form[key]} onChange={(event) => setForm({ ...form, [key]: event.target.value })}
          className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3" />
      </label>)}
      {editingId && <label className="text-sm font-medium text-slate-700">Lifecycle status
        <select value={form.lifecycle_status} onChange={(event) => setForm({ ...form, lifecycle_status: event.target.value })}
          className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3">
          <option value="active">Active</option><option value="suspended">Suspended</option><option value="archived">Archived</option>
        </select>
      </label>}
      {[['max_users','Maximum users'],['max_readers','Maximum readers'],['max_cameras','Maximum cameras'],['max_edge_agents','Maximum edge agents']].map(([key,label]) =>
        <label key={key} className="text-sm font-medium text-slate-700">{label}
          <input type="number" min={key === 'max_users' ? 1 : 0} value={form[key]}
            onChange={(event) => setForm({ ...form, [key]: Number(event.target.value) })}
            className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3" />
        </label>)}
      <div className="md:col-span-2 xl:col-span-3"><p className="text-sm font-medium text-slate-700">Licensed modules</p>
        <div className="mt-2 flex flex-wrap gap-3">{availableModules.map((module) => <label key={module} className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm capitalize">
          <input type="checkbox" checked={form.licensed_modules.includes(module)} onChange={(event) => setForm({ ...form,
            licensed_modules: event.target.checked ? [...form.licensed_modules, module] : form.licensed_modules.filter((item) => item !== module) })}/>{module}
        </label>)}</div>
      </div>
      <button disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-teal-700 px-5 py-3 font-semibold text-white disabled:opacity-60">
        <Plus size={17}/>{saving ? 'Saving...' : editingId ? 'Save Entity Controls' : 'Create Entity'}
      </button>
      {editingId && <button type="button" onClick={() => { setEditingId(null); setForm(initialForm); }} className="rounded-2xl border border-slate-300 px-5 py-3">Cancel</button>}
    </form>
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {entities.map((entity) => <div key={entity.id} className="app-surface rounded-[1.5rem] p-5">
        <div className="flex items-center gap-3"><Building2 className="text-teal-700"/><div><h2 className="font-semibold text-slate-900">{entity.name}</h2><p className="font-mono text-xs text-slate-500">{entity.code}</p></div></div>
        <p className="mt-4 text-sm text-slate-500 capitalize">{entity.category} · {entity.lifecycle_status || (entity.is_active ? 'active' : 'inactive')}</p>
        <p className="mt-1 text-xs text-slate-500">{countries.find((country) => country.code === entity.country_code)?.name || entity.country_code} · {entity.timezone}</p>
        <p className="mt-2 text-xs text-slate-500">Modules: {(entity.licensed_modules || []).join(', ') || 'None'}</p>
        <p className="mt-1 text-xs text-slate-500">Limits: {entity.max_users} users · {entity.max_readers} readers · {entity.max_cameras} cameras · {entity.max_edge_agents} agents</p>
        <p className="mt-1 text-xs text-slate-500">Subscription: {entity.subscription_starts_on?.slice(0,10) || 'Immediate'} to {entity.subscription_ends_on?.slice(0,10) || 'No expiry'}</p>
        <div className="mt-4 flex flex-wrap gap-2"><button onClick={() => edit(entity)} className="inline-flex items-center gap-1 rounded-xl border px-3 py-2 text-xs"><Pencil size={13}/>Manage</button>
          <button onClick={() => resetPassword(entity)} className="inline-flex items-center gap-1 rounded-xl border px-3 py-2 text-xs"><KeyRound size={13}/>Reset admin password</button></div>
      </div>)}
    </div>
  </div>;
};

export default Entities;
